import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import InputText from 'components/TextInput/InputText';
import Button from 'components/Button/Button';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { isArrayEmpty, isObjectEmpty } from 'helpers/checkEmptiness';
import { EmployeeWorkInput, StaffReportModel } from '../../models';
import { UserCredential } from '../../../../login/models';
import BasicSelectInput, {
    Option,
    Options,
} from '../../../../../components/SelectInput/BasicSelectInput';
import { StaffReportStatusEnum, typeOptions } from '../../constants';
import BasicDatePicker from '../../../../../components/DatePicker/BasicDatePicker';
import SelectInput from '../../../../../components/SelectInput/SelectInput';
import { Job } from '../../../../worksheet/process/job/models';
import { RootState } from '../../../../../app/store';
import { MultiValue } from 'react-select';
import { dateSetTimeToNow } from '../../../../../helpers/convert';
import {
    createStaffReport,
    updateStaffReport,
} from '../../staffReport.reducer';
import AsyncSelect from 'react-select/async';
import {
    getJobDropdown,
    getJobDropdownReport,
} from '../../../../worksheet/process/job/job.reducer';
import { unwrapResult } from '@reduxjs/toolkit';
const ApproveEmployeeWorkForm = ({
    singleEmployeeWork = {} as StaffReportModel,
    userId = '',
    positionId = '',
    isEdit = false,
    onClose,
}: ApproveEmployeeWorkFormProps) => {
    const dispatch = useAppDispatch();
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<EmployeeWorkInput>({
        defaultValues: {
            job_number: [],
            requestDate:
                dateSetTimeToNow(new Date().toISOString()) ??
                new Date().toISOString(),
        },
    });

    const watchJobNumber = watch('job_number');

    const jobSummary = (watchJobNumber as any[])?.reduce(
        (acc, curr) => {
            const data = curr.jobData;
            if (!data) return acc;
            return {
                totalReqPole: acc.totalReqPole + (data.totalReqPole || 0),
                totalPrep: acc.totalPrep + (data.totalPrep || 0),
                totalCalcspec: acc.totalCalcspec + (data.totalCalcspec || 0),
                totalQC: acc.totalQC + (data.totalQC || 0),
                totalKatapult: acc.totalKatapult + (data.totalKatapult || 0),
                totalMRE: acc.totalMRE + (data.totalMRE || 0),
                totalMREQC: acc.totalMREQC + (data.totalMREQC || 0),
            };
        },
        {
            totalReqPole: 0,
            totalPrep: 0,
            totalCalcspec: 0,
            totalQC: 0,
            totalKatapult: 0,
            totalMRE: 0,
            totalMREQC: 0,
        },
    );

    const watchReqPole = watch('reqPole');
    const watchReqMinute = watch('reqMinute');
    const watchType = watch('type');

    const totalAssignedPoles =
        (jobSummary?.totalPrep || 0) +
        (jobSummary?.totalCalcspec || 0) +
        (jobSummary?.totalQC || 0) +
        (jobSummary?.totalKatapult || 0) +
        (jobSummary?.totalMRE || 0) +
        (jobSummary?.totalMREQC || 0);

    const isTypeAdd = String(watchType) === '0';
    const isTypeOvercapacity = String(watchType) === '5';

    const inputPoles = Number(watchReqPole || 0);
    const maxReqMinutes = isTypeOvercapacity
        ? inputPoles * 15
        : Math.ceil(inputPoles / 10) * 15;

    const user = useAppSelector(
        (state: RootState) => state.user.userAuth ?? ({} as UserCredential),
    );

    const isRoleQC = user?.roleName?.includes('QC');
    const isRoleSPV = user?.roleName?.includes('SPV');
    const isAssignedQC = (jobSummary?.totalQC || 0) > 0;
    const shouldApplyAddLimit =
        isTypeAdd && isRoleQC && isAssignedQC && !isRoleSPV;
    const shouldApplyOvercapacityLimit = isTypeOvercapacity && !isRoleSPV;

    const isReqPoleError =
        shouldApplyAddLimit && Number(watchReqPole || 0) > totalAssignedPoles;
    const isReqMinuteError =
        (shouldApplyAddLimit || shouldApplyOvercapacityLimit) &&
        Number(watchReqMinute || 0) > maxReqMinutes;
    const isZeroPoles =
        (shouldApplyAddLimit || shouldApplyOvercapacityLimit) &&
        inputPoles === 0;
    const isOverLimitError = isReqPoleError || isReqMinuteError || isZeroPoles;

    const [jobNumberOptions, setJobNumberOptions] = useState<Array<Option>>([]);
    const [hide, setHide] = useState<string | undefined>(undefined);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const jobListState = useAppSelector(
        (state: RootState) => state.job.list ?? [],
    );

    useEffect(() => {
        if (!isObjectEmpty(singleEmployeeWork)) {
            const selectedJobNumber = singleEmployeeWork?.number_job
                ?.split(',')
                .map((jobNumber: string) => {
                    return {
                        text: jobNumber,
                        label: jobNumber,
                        value: jobNumber,
                    };
                });
            reset({
                requestDate:
                    singleEmployeeWork.requestDate === '0001-01-01T00:00:00'
                        ? new Date().toISOString()
                        : singleEmployeeWork.requestDate,
                type: singleEmployeeWork.type.toString(),
                reqMinute: singleEmployeeWork.reqMinute.toString(),
                reqPole: singleEmployeeWork.reqPole.toString(),
                note: singleEmployeeWork.note,
                job_number: selectedJobNumber,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleEmployeeWork]);

    useEffect(() => {
        if (!isArrayEmpty(jobListState)) {
            let _options = jobListState.map((job: Job) => {
                return {
                    text: job.number_job,
                    label: job.number_job,
                    value: job.number_job,
                };
            });
            setJobNumberOptions([..._options]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobListState]);

    const onSubmit = (data: EmployeeWorkInput) => {
        let _input = {
            number_job: data.job_number
                .map(
                    (job: Option) =>
                        (job as any).jobData?.number_job || job.value,
                )
                .join(','),
            role_id: positionId,
            note: data.note,
            reqMinute: parseInt(data.reqMinute),
            reqPole: parseInt(data.reqPole),
            approvalMinute: 0,
            approvalPole: 0,
            rejectionNote: '',
            status: StaffReportStatusEnum.Requested,
            type: parseInt(data.type),
            requestDate: data.requestDate,
            userId: userId,
        };
        if (isEdit) {
            let update = { ..._input, id: singleEmployeeWork?.id };
            dispatch(updateStaffReport(update));
        } else {
            dispatch(createStaffReport(_input));
        }
    };

    const onJobNumberChange = (val: MultiValue<Option>) => {
        setValue('job_number', val);
    };

    const getDropdownJob = async (inputValue: string) => {
        let result;
        if (String(watchType) === '0' && !isRoleSPV) {
            result = await dispatch(
                getJobDropdownReport({
                    skip: 0,
                    take: 100,
                    job_number:
                        inputValue.toLowerCase() === ''
                            ? 'a'
                            : inputValue.toLowerCase(),
                }),
            );
        } else {
            result = await dispatch(
                getJobDropdown({
                    skip: 0,
                    take: 100,
                    job_number:
                        inputValue.toLowerCase() === ''
                            ? 'a'
                            : inputValue.toLowerCase(),
                }),
            );
        }

        const originalPromiseResult = unwrapResult(result);

        let _option;
        if (String(watchType) === '0' && !isRoleSPV) {
            _option = originalPromiseResult?.data
                ?.filter((item: any) => {
                    return (
                        item.totalPrep !== 0 ||
                        item.totalCalcspec !== 0 ||
                        item.totalQC !== 0 ||
                        item.totalKatapult !== 0 ||
                        item.totalMRE !== 0 ||
                        item.totalMREQC !== 0
                    );
                })
                .map((item: any) => {
                    return {
                        label: `${item.number_job} | ${item.other_number} | Total Pole: ${item.totalReqPole}`,
                        text: `${item.number_job} | ${item.other_number}`,
                        value: item.id,
                        jobData: item,
                    };
                });
        } else {
            _option = originalPromiseResult?.data?.map((item: any) => {
                return {
                    label: `${item.number_job} | ${item.other_number}`,
                    text: `${item.number_job} | ${item.other_number}`,
                    value: item.id,
                    jobData: item,
                };
            });
        }
        return _option as Options[];
    };

    const promiseOptions = (
        inputValue: string,
        callback: (options: Options[]) => void,
    ) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            try {
                const res = await getDropdownJob(inputValue);
                callback(res);
            } catch (error) {
                callback([]);
            }
        }, 1000);
    };

    return (
        <div className="p-2 md:p-6 pt-2 md:pt-2 bg-white dark:border-gray-700 dark:bg-gray-800 rounded-2xl">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div>
                        <BasicDatePicker
                            label="Request Date"
                            name="requestDate"
                            placeholderText="Select Request Date"
                            control={control}
                            withPreciseTime
                        />
                        <div className="my-2">
                            <BasicSelectInput
                                label="Type"
                                message={errors.type?.message}
                                options={typeOptions}
                                {...register('type')}
                            />
                            <div className="text-xs text-gray-500 -mt-2 mb-2">
                                Add Pole based on assigned poles
                            </div>
                        </div>

                        <div className="my-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Job Number (CE)
                                </label>
                            </div>
                            <div>
                                <AsyncSelect
                                    key={watchType}
                                    cacheOptions
                                    defaultOptions
                                    isMulti
                                    loadOptions={promiseOptions}
                                    value={watchJobNumber}
                                    onChange={onJobNumberChange}
                                    styles={{
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 9999,
                                        }),
                                    }}
                                />
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                                Only jobs assigned to you are shown
                            </div>

                            {/* Total Poles Info Box */}
                            {(watchJobNumber as any[])?.length > 0 && (
                                <div className="mt-4 p-4 border border-teal-500 rounded-lg bg-teal-50 flex flex-col gap-2">
                                    <div className="flex justify-between items-center text-sm font-medium text-gray-800 border-b border-teal-200 pb-2 mb-1">
                                        <span>Total Poles</span>
                                        <span>
                                            {' '}
                                            {jobSummary?.totalReqPole || 0}{' '}
                                            Poles
                                        </span>
                                    </div>
                                    <div className="text-sm font-semibold text-teal-800">
                                        Assigned to You
                                    </div>
                                    {!!jobSummary?.totalPrep && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>Prep</span>
                                            <span> {jobSummary.totalPrep}</span>
                                        </div>
                                    )}
                                    {!!jobSummary?.totalCalcspec && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>CS</span>
                                            <span>
                                                {' '}
                                                {jobSummary.totalCalcspec}
                                            </span>
                                        </div>
                                    )}
                                    {!!jobSummary?.totalQC && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>QC</span>
                                            <span> {jobSummary.totalQC}</span>
                                        </div>
                                    )}
                                    {!!jobSummary?.totalKatapult && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>Katapult</span>
                                            <span>
                                                {' '}
                                                {jobSummary.totalKatapult}
                                            </span>
                                        </div>
                                    )}
                                    {!!jobSummary?.totalMRE && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>MRE</span>
                                            <span> {jobSummary.totalMRE}</span>
                                        </div>
                                    )}
                                    {!!jobSummary?.totalMREQC && (
                                        <div className="flex justify-between items-center text-sm text-gray-700">
                                            <span>QC MRE</span>
                                            <span>
                                                {' '}
                                                {jobSummary.totalMREQC}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="my-2 mt-4">
                            <InputText
                                {...register('reqPole')}
                                label="Total Poles"
                                placeholder="Input total poles"
                                type="number"
                                message={errors.reqPole?.message}
                            />
                            {isReqPoleError && (
                                <div className="text-xs text-red-500 mt-1 font-medium">
                                    Value exceeds Total Assigned Poles (
                                    {totalAssignedPoles})
                                </div>
                            )}
                        </div>

                        <div className="my-2">
                            <InputText
                                {...register('reqMinute')}
                                label="Minutes"
                                placeholder="Input request minute"
                                type="number"
                                message={errors.reqMinute?.message}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {isTypeOvercapacity
                                    ? '15 minutes per 1 pole'
                                    : '15 minutes per 10 poles'}
                            </div>
                            {isReqMinuteError && (
                                <div className="text-xs text-red-500 mt-1 font-medium">
                                    Value exceeds Maximum Minutes (
                                    {maxReqMinutes})
                                </div>
                            )}
                        </div>

                        <div className="my-2 mt-4">
                            <InputText
                                {...register('note')}
                                label="Notes"
                                placeholder="Input notes"
                                message={errors.note?.message}
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="flex flex-col gap-6 mt-6 md:mt-0">
                        {/* Your Limit Card */}
                        {(shouldApplyAddLimit ||
                            shouldApplyOvercapacityLimit) && (
                            <div className="border border-teal-600 rounded-lg p-4">
                                <h3 className="text-teal-700 font-semibold mb-4 pb-2 border-b border-gray-200">
                                    Your Limit (Add Pole)
                                </h3>

                                <div className="space-y-3 mb-6">
                                    {!isTypeOvercapacity && (
                                        <div className="flex text-sm text-gray-700">
                                            <span className="w-1/2">
                                                Assigned Poles
                                            </span>
                                            <span className="w-1/2 font-medium">
                                                : {totalAssignedPoles} poles
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex text-sm text-gray-700">
                                        <span className="w-1/2">
                                            Calculation
                                        </span>
                                        <span className="w-1/2 font-medium">
                                            :{' '}
                                            {isTypeOvercapacity
                                                ? `${inputPoles} x 15`
                                                : `(${inputPoles} / 10) x 15`}
                                        </span>
                                    </div>
                                    <div className="flex text-sm text-gray-700">
                                        <span className="w-1/2">
                                            Maximum Minutes
                                        </span>
                                        <span className="w-1/2 font-medium">
                                            :{' '}
                                            {isTypeOvercapacity ? (
                                                <span className="text-teal-700 font-bold">
                                                    {maxReqMinutes} minutes
                                                </span>
                                            ) : (
                                                <>
                                                    {(inputPoles / 10) * 15}{' '}
                                                    <span className="text-teal-700 mx-1">
                                                        &rarr;
                                                    </span>{' '}
                                                    <span className="text-teal-700 font-bold">
                                                        {maxReqMinutes} minutes
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <div className="flex justify-between text-xs font-medium text-gray-700 mb-1">
                                        <span>
                                            {watchReqMinute || 0} /{' '}
                                            {maxReqMinutes} minutes used
                                        </span>
                                        <span>
                                            {maxReqMinutes > 0
                                                ? Math.min(
                                                      100,
                                                      Math.round(
                                                          ((Number(
                                                              watchReqMinute,
                                                          ) || 0) /
                                                              maxReqMinutes) *
                                                              100,
                                                      ),
                                                  )
                                                : 0}
                                            %
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div
                                            className={`h-3 rounded-full ${isReqMinuteError ? 'bg-red-500' : 'bg-teal-600'}`}
                                            style={{
                                                width: `${maxReqMinutes > 0 ? Math.min(100, Math.round(((Number(watchReqMinute) || 0) / maxReqMinutes) * 100)) : 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Overcapacity / Recommendation Card */}
                        {/* <div className="border border-blue-600 rounded-lg p-4">
                            <h3 className="text-blue-700 font-semibold mb-4 pb-2 border-b border-gray-200">
                                Overcapacity / Recommendation (Optional)
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Overcapacity Qty
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            defaultValue="2"
                                            readOnly
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-gray-50"
                                        />
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">
                                            activity
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Recommendation / Underset Qty
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            defaultValue="1"
                                            readOnly
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-gray-50"
                                        />
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">
                                            activity
                                        </span>
                                    </div>
                                </div>

                                <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
                                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                                        <li>15 minutes per Overcapacity</li>
                                        <li>
                                            15 minutes per Recommendation /
                                            Underset
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-orange-50 border border-orange-300 rounded-lg p-3">
                                    <p className="text-sm text-gray-700">
                                        Maximum additional minutes from
                                    </p>
                                    <p className="text-sm font-semibold text-gray-900 mt-1">
                                        Overcapacity + Recommendation : 45
                                        minutes
                                    </p>
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>

                <div className="pt-6 pb-2 flex justify-end gap-3 mt-6 border-t border-gray-200 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isOverLimitError}
                        className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${isOverLimitError ? 'bg-gray-400 cursor-not-allowed' : 'bg-teal-700 hover:bg-teal-800'} focus:outline-none`}
                    >
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
};

export interface ApproveEmployeeWorkFormProps {
    singleEmployeeWork?: StaffReportModel;
    userId?: string;
    positionId?: string;
    isEdit?: boolean;
    onClose?: () => void;
}

export default ApproveEmployeeWorkForm;
